/**
 * LeadCard — compact white card. `LeadCardView` is the pure presentation
 * (also used by the DragOverlay); `DraggableLeadCard` wires dnd-kit.
 * Readable text is 12 px or larger and `text-muted` or darker; the
 * qualification is a dot PLUS a short word, never colour alone.
 */
import { useDraggable } from '@dnd-kit/core';
import { BellRing, ClipboardCheck, FileText, PhoneCall, StickyNote } from 'lucide-react';
import { useShops } from '@/lib/shop-store';
import { shopMetaFor } from '@/lib/shop-colors';
import { daysLabel } from '@/lib/time';
import { assessmentGlyphLabel } from '@/features/leads/assessment-format';
import { screeningGlyphLabel } from '@/features/screening/screening-format';
import { fullName, truthy, type LeadCard as LeadCardData, type Qualification } from './types';

// `text-warning-text` (#B45309) is the readable warning colour; `text-warning`
// (#E65100) is 3.5:1 on white and stays on the dot only (Stage 3 QA carry-over).
const QUAL: Record<Exclude<Qualification, null> | 'none', { dot: string; label: string; short: string; text: string }> = {
  none: { dot: 'bg-gray-400', label: 'Not screened yet', short: 'Unscreened', text: 'text-muted' },
  qualified: { dot: 'bg-success', label: 'Qualified', short: 'Qualified', text: 'text-success' },
  needs_review: { dot: 'bg-warning', label: 'Needs review', short: 'Review', text: 'text-warning-text' },
  not_qualified: { dot: 'bg-danger', label: 'Not qualified', short: 'Unqualified', text: 'text-danger' },
};

export function qualificationMeta(q: Qualification) {
  return QUAL[q ?? 'none'];
}

export function useShopName(shopId: string | null): string | null {
  const { data: shops } = useShops();
  if (!shopId) return null;
  return shops?.find((s) => s.id === shopId)?.name ?? 'Shop';
}

export function LeadCardView({ lead, lifted = false }: { lead: LeadCardData; lifted?: boolean }) {
  const shopName = useShopName(lead.shop_id);
  const qual = qualificationMeta(lead.qualification);
  const days = daysLabel(lead.stage_entered_at);
  const assessment = assessmentGlyphLabel(lead.assessment_status);
  const screening = screeningGlyphLabel(lead.screening_status);

  return (
    <div
      className={[
        'w-full rounded-lg border bg-white px-3 py-2.5 text-left',
        lifted ? 'border-navy shadow-xl rotate-[1.5deg]' : 'border-gray-200 shadow-sm',
      ].join(' ')}
    >
      <div className="truncate text-[13px] font-semibold leading-5 text-gray-900">{fullName(lead)}</div>
      <div className="truncate text-[12px] leading-4 text-gray-700">
        {lead.role_title}
        {lead.alias_title && lead.alias_title !== lead.role_title && (
          <span className="text-muted"> · {lead.alias_title}</span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px]">
        <span className={`inline-flex items-center gap-1 font-medium ${qual.text}`} title={qual.label}>
          <span className={`h-2 w-2 rounded-full ${qual.dot}`} aria-hidden />
          {qual.short}
        </span>
        {lead.shop_id && shopName ? (
          <span className={`rounded px-1.5 py-0.5 font-medium ${shopMetaFor(lead.shop_id, shopName).pill}`}>
            {shopName}
          </span>
        ) : (
          <span className="rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-muted">Unassigned</span>
        )}
        {lead.source && (
          <span className="rounded bg-page px-1.5 py-0.5 font-medium text-gray-700">{lead.source}</span>
        )}
        <span className="ml-auto flex items-center gap-1.5 text-muted">
          {truthy(lead.nudge_pending) && (
            <BellRing size={12} strokeWidth={2} role="img" aria-label="Nudge awaiting reply" className="text-warning-text" />
          )}
          {screening && (
            <PhoneCall size={12} strokeWidth={2} role="img" aria-label={screening} className="text-info-blue" />
          )}
          {truthy(lead.has_resume) && <FileText size={12} strokeWidth={2} role="img" aria-label="Has resume" />}
          {assessment && (
            <ClipboardCheck
              size={12}
              strokeWidth={2}
              role="img"
              aria-label={assessment}
              className={lead.assessment_status === 'completed' ? 'text-success' : undefined}
            />
          )}
          {lead.note_count > 0 && (
            <span className="flex items-center gap-0.5" aria-label={`${lead.note_count} notes`}>
              <StickyNote size={12} strokeWidth={2} aria-hidden />
              {lead.note_count}
            </span>
          )}
          {days && (
            <span title="Time in this stage" className="tabular-nums">
              {days}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

export function DraggableLeadCard({
  lead,
  onOpen,
}: {
  lead: LeadCardData;
  onOpen: (id: string) => void;
}) {
  // The DragOverlay in Board renders the moving copy; the original stays put
  // as a faded placeholder. (Translating it too would put it under the
  // pointer at drop time, so the pointerup turned into a click that opened
  // the drawer after every drag.) No aria-label: the card's own text is the
  // accessible name, so a screen reader hears name, role, shop and status.
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...listeners}
      onClick={() => onOpen(lead.id)}
      data-testid={`lead-card-${lead.id}`}
      className={[
        'block w-full rounded-lg text-left touch-manipulation cursor-grab active:cursor-grabbing',
        'hover:[&>div]:border-navy/40',
        isDragging ? 'opacity-30' : '',
      ].join(' ')}
    >
      <LeadCardView lead={lead} />
    </button>
  );
}
